import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';

const Videography: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/');
  };

  const videoProjects = [
    {
      title: "Medical Procedure Documentation",
      duration: "15:32",
      resolution: "4K",
      status: "Completed",
      category: "Medical"
    },
    {
      title: "Drone Cinematography Reel",
      duration: "03:45",
      resolution: "4K",
      status: "Rendering",
      category: "Aerial"
    },
    {
      title: "Tech Product Showcase",
      duration: "08:21",
      resolution: "1080p",
      status: "Editing",
      category: "Commercial"
    },
    {
      title: "Event Highlights",
      duration: "12:15",
      resolution: "4K",
      status: "Completed",
      category: "Event"
    },
    {
      title: "Mobile Photography Tutorial",
      duration: "06:33",
      resolution: "1080p",
      status: "Published",
      category: "Educational"
    },
    {
      title: "Time-lapse Collection",
      duration: "04:12",
      resolution: "4K",
      status: "Completed",
      category: "Creative"
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return 'text-green-400';
      case 'Published': return 'text-blue-400';
      case 'Rendering': return 'text-yellow-400';
      case 'Editing': return 'text-orange-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-rajdhani relative overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 z-[1] opacity-30">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 to-black"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 pt-24 pb-16 px-4">
        <BackButton onClick={handleBack} />
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="text-purple-400 font-mono text-lg mb-4 cyber-glow">
            bash@neural-core:~$ ls -la /media_production/video/
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            VIDEO_PRODUCTION
          </h1>
          <p className="text-gray-400 text-xl max-w-4xl mx-auto">
            Motion Graphics • Cinematography • Post-Production • Digital Storytelling
          </p>
        </motion.div>

        <div className="max-w-7xl mx-auto">
          {/* Production Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-purple-900/20 to-purple-700/10 border border-purple-400/30 p-8 rounded-lg mb-8"
          >
            <h2 className="text-purple-400 font-mono text-2xl mb-6">PRODUCTION_STATS</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl text-white font-bold mb-2">47</div>
                <div className="text-gray-400">Total Videos</div>
              </div>
              <div className="text-center">
                <div className="text-3xl text-green-400 font-bold mb-2">123h</div>
                <div className="text-gray-400">Content Created</div>
              </div>
              <div className="text-center">
                <div className="text-3xl text-blue-400 font-bold mb-2">4K</div>
                <div className="text-gray-400">Max Resolution</div>
              </div>
              <div className="text-center">
                <div className="text-3xl text-purple-400 font-bold mb-2">8</div>
                <div className="text-gray-400">Categories</div>
              </div>
            </div>
          </motion.div>

          {/* Video List */}
          <div className="grid gap-4 mb-8">
            {videoProjects.map((video, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + (i * 0.1) }}
                className="bg-black/60 border border-purple-400/30 p-6 rounded-lg hover:border-purple-400 transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Play Button */}
                    <div className="w-12 h-12 bg-purple-500/80 rounded-full flex items-center justify-center group-hover:bg-purple-500 transition-colors">
                      <div className="w-0 h-0 border-l-4 border-l-white border-t-2 border-t-transparent border-b-2 border-b-transparent ml-1"></div>
                    </div>
                    
                    {/* Video Info */}
                    <div>
                      <h3 className="text-white font-semibold text-lg">{video.title}</h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-400">
                        <span>{video.duration}</span>
                        <span>{video.resolution}</span>
                        <span className="text-purple-400">{video.category}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Status */}
                  <div className="text-right">
                    <div className={`${getStatusColor(video.status)} font-mono text-sm`}>
                      {video.status}
                    </div>
                    <div className="text-gray-500 text-xs mt-1">
                      {video.status === 'Published' ? 'LIVE' : 'LOCAL'}
                    </div>
                  </div>
                </div>
                
                {/* Progress Bar for non-completed videos */}
                {(video.status === 'Rendering' || video.status === 'Editing') && (
                  <div className="mt-4">
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <motion.div
                        className={`h-2 rounded-full ${
                          video.status === 'Rendering' ? 'bg-yellow-400' : 'bg-orange-400'
                        }`}
                        initial={{ width: 0 }}
                        animate={{ width: video.status === 'Rendering' ? '75%' : '45%' }}
                        transition={{ delay: 1 + (i * 0.1), duration: 1.5 }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {video.status === 'Rendering' ? 'Rendering in progress...' : 'Post-production in progress...'}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Equipment & Software */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Equipment */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9 }}
              className="bg-black/60 border border-green-400/30 p-6 rounded-lg"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <h3 className="text-green-400 font-mono text-xl">EQUIPMENT</h3>
              </div>
              
              <div className="space-y-4">
                {[
                  { name: "Canon EOS R6 Mark II", type: "Primary Camera", status: "Active" },
                  { name: "Sony A7S III", type: "Low Light Specialist", status: "Standby" },
                  { name: "DJI Mini 3 Pro", type: "Aerial Cinematography", status: "Ready" },
                  { name: "iPhone 15 Pro Max", type: "Mobile Production", status: "Active" }
                ].map((equipment, i) => (
                  <div key={i} className="flex justify-between items-center p-3 border-l-2 border-green-400/50">
                    <div>
                      <div className="text-white font-semibold">{equipment.name}</div>
                      <div className="text-gray-400 text-sm">{equipment.type}</div>
                    </div>
                    <div className="text-green-400 text-xs">{equipment.status}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Software */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.0 }}
              className="bg-black/60 border border-blue-400/30 p-6 rounded-lg"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
                <h3 className="text-blue-400 font-mono text-xl">SOFTWARE_SUITE</h3>
              </div>
              
              <div className="space-y-4">
                {[
                  { name: "Adobe Premiere Pro 2024", type: "Primary Editor", usage: "90%" },
                  { name: "After Effects 2024", type: "Motion Graphics", usage: "75%" },
                  { name: "DaVinci Resolve", type: "Color Grading", usage: "60%" },
                  { name: "Final Cut Pro", type: "Quick Edits", usage: "45%" }
                ].map((software, i) => (
                  <div key={i} className="p-3 border-l-2 border-blue-400/50">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <div className="text-white font-semibold">{software.name}</div>
                        <div className="text-gray-400 text-sm">{software.type}</div>
                      </div>
                      <div className="text-blue-400 text-xs">{software.usage}</div>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-1">
                      <motion.div
                        className="h-1 rounded-full bg-blue-400"
                        initial={{ width: 0 }}
                        animate={{ width: software.usage }}
                        transition={{ delay: 1.2 + (i * 0.1), duration: 1 }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Videography;